import {
  Button,
  LoadingOverlay,
  PasswordInput,
  Stack,
  Text,
  Title
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { useMutation } from '@tanstack/react-query'
import { FormattedMessage, useIntl } from 'react-intl'
import { useNavigate, useSearchParams } from 'react-router'
import { accountApi } from '@/api/accountApi'
import { extractErrorMessage } from '@/api/helpers/extractErrorMessage'
import { AuthLayout } from '@/components/layout/AuthLayout/AuthLayout'
import { PasswordRules } from '@/components/ui-shared/PasswordRules/PasswordRules'
import { PlatformAlert } from '@/components/ui-shared/PlatformAlert/PlatformAlert'
import { RouterPath } from '@/router/paths'
import { useSession } from '@/session/SessionContext'
import { useLogout } from '@/session/useLogout'
import { showToast } from '@/theme/notifications'
import { isStrongPassword } from '@/utils/validation/password'

type FormValues = {
  currentPassword: string
  password: string
  confirmPassword: string
}

export const ChangePasswordPage = () => {
  const intl = useIntl()
  const navigate = useNavigate()
  const { refreshSession } = useSession()
  const { logout } = useLogout()
  const [searchParams] = useSearchParams()
  const isExpired = searchParams.get('reason') === 'expired'

  const form = useForm<FormValues>({
    initialValues: {
      currentPassword: '',
      password: '',
      confirmPassword: ''
    },
    validate: {
      password: isStrongPassword(
        intl.formatMessage({ id: 'validation.strongPassword.invalid' })
      ),
      confirmPassword: (value, values) =>
        value !== values.password
          ? intl.formatMessage({ id: 'validation.passwordsDoNotMatch' })
          : null
    }
  })

  const { mutate, isPending } = useMutation({
    mutationFn: ({
      password,
      currentPassword
    }: Pick<FormValues, 'password' | 'currentPassword'>) =>
      accountApi.changePassword(password, currentPassword),
    onSuccess: async () => {
      showToast(intl.formatMessage({ id: 'changePassword.success' }), 'success')
      await refreshSession()
      void navigate(RouterPath.root)
    },
    onError: (error) => {
      showToast(
        extractErrorMessage(
          error,
          intl.formatMessage({ id: 'changePassword.error' })
        ),
        'error'
      )
    }
  })

  const handleSwitchAccount = async () => {
    await logout()
    void navigate(RouterPath.login)
  }

  return (
    <AuthLayout>
      <Stack pos="relative" gap="lg">
        <LoadingOverlay visible={isPending} />

        <Title order={2}>
          <FormattedMessage id="changePassword.title" />
        </Title>

        {isExpired && (
          <PlatformAlert variant="info">
            <FormattedMessage id="changePassword.expiredNotice" />
          </PlatformAlert>
        )}

        <form
          noValidate
          onSubmit={form.onSubmit(({ password, currentPassword }) =>
            mutate({ password, currentPassword })
          )}
        >
          <Stack gap="lg">
            <PasswordInput
              label={<FormattedMessage id="changePassword.currentPassword" />}
              {...form.getInputProps('currentPassword')}
            />

            <Stack gap="sm">
              <PasswordInput
                label={<FormattedMessage id="changePassword.newPassword" />}
                {...form.getInputProps('password')}
              />
              <PasswordRules password={form.values.password} />
            </Stack>

            <PasswordInput
              label={
                <FormattedMessage id="changePassword.confirmNewPassword" />
              }
              {...form.getInputProps('confirmPassword')}
            />

            <Stack gap="xs">
              <Button type="submit" fullWidth>
                <FormattedMessage id="changePassword.submit" />
              </Button>

              {isExpired ? (
                <Stack gap="xs">
                  <Button
                    variant="subtle"
                    size="sm"
                    fullWidth
                    onClick={() => void handleSwitchAccount()}
                  >
                    <FormattedMessage id="logout" />
                  </Button>
                  <Text size="xs" c="dimmed" ta="center">
                    <FormattedMessage id="changePassword.switchAccountHint" />
                  </Text>
                </Stack>
              ) : (
                <Button
                  variant="subtle"
                  size="sm"
                  fullWidth
                  onClick={() => void navigate(-1)}
                >
                  <FormattedMessage id="goBack" />
                </Button>
              )}
            </Stack>
          </Stack>
        </form>
      </Stack>
    </AuthLayout>
  )
}
